import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type { JobTypeDisplay } from "@/services/job/job.types";
import {
	type ConfigurationTypeTableAccessDisplay,
	type ConfigurationTypeTableAccessKeys,
	getConfigurationTypeTableAccessDisplay,
	getConfigurationTypeTableAccessTooltip,
} from "@/utils/configuration-type-table-access/configuration-type-table-access.utils";

export type JobTypeScope = "platform" | "organization";

export function normalizeJobTypeSource(row: JobTypeDisplay): string {
	if (typeof row.defaultType === "boolean") {
		return row.defaultType ? "system" : "custom";
	}
	return (row.source ?? "").trim().toLowerCase();
}

export function isSystemJobType(row: JobTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return row.defaultType;
	const source = (row.source ?? "").trim().toLowerCase();
	return source === "" || source === "system";
}

export function isCustomJobType(row: JobTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return !row.defaultType;
	return (row.source ?? "").trim().toLowerCase() === "custom";
}

export function canShowDeleteJobType(
	scope: JobTypeScope,
	row: JobTypeDisplay,
): boolean {
	if (scope === "platform") return isSystemJobType(row);
	return isCustomJobType(row);
}

export function canShowActivityTab(
	scope: JobTypeScope,
	_row: JobTypeDisplay,
): boolean {
	if (scope === "platform" || scope === "organization") return true;
	return false;
}

export function canShowAccessTab(scope: JobTypeScope): boolean {
	return scope === "organization";
}

export function getJobTypeDisplayName(row: JobTypeDisplay): string {
	return row.name?.trim() || "";
}

/** Org-wide disabled when `enabled === false`. */
export function isJobTypeOrgAccessDisabled(
	row: Pick<JobTypeDisplay, "enabled">,
): boolean {
	return row.enabled === false;
}

/**
 * Org-admin configuration table: all types remain visible when org-disabled
 * so Access tab changes are reflected in the table (e.g. "Not available").
 */
export function shouldShowOrgAdminJobTypesTableRow(
	_row: Pick<JobTypeDisplay, "defaultType" | "enabled" | "source">,
): boolean {
	return true;
}

export type JobTypeTableAccessDisplay = ConfigurationTypeTableAccessDisplay;

const JOB_TYPE_TABLE_ACCESS_KEYS = {
	available: "admin.jobTypes.table.access.available",
	notAvailable: "admin.jobTypes.table.access.notAvailable",
	noSubOrganizations: "org-admin.jobTypes.table.access.noSubOrganizations",
	allSubOrganizations: "org-admin.jobTypes.table.access.allSubOrganizations",
	partial: "admin.jobTypes.table.access.partial",
	tooltipNone: "admin.jobTypes.table.access.tooltipNone",
} satisfies ConfigurationTypeTableAccessKeys;

/** Org-admin Job Types table/export Access column label and badge color. */
export function getJobTypeTableAccessDisplay(
	row: JobTypeDisplay,
	totalSubOrganizations: number,
	t: AppTranslateFn,
): JobTypeTableAccessDisplay {
	return getConfigurationTypeTableAccessDisplay({
		enabled: row.enabled,
		grantedCount: row.jobTypeSubOrganizations?.length ?? 0,
		totalSubOrganizations,
		keys: JOB_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}

/** Org-admin Job Types table Access column hover (sub-org names or "none"). */
export function getJobTypeTableAccessTooltip(
	row: JobTypeDisplay,
	t: AppTranslateFn,
): string {
	return getConfigurationTypeTableAccessTooltip({
		grantedSubOrganizations: row.jobTypeSubOrganizations ?? [],
		keys: JOB_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}
